import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../utils/api';
import { getQuotaGroup } from './quotaUtils';

const STALE_PENDING_MS = 3 * 86400000; // 3 days

function getProviderRequestUrl(provider: string, region: string, _requestId: string): string {
  if (provider === 'gcp') {
    return 'https://console.cloud.google.com/iam-admin/quotas';
  }
  // AWS Service Quotas console for the region
  return `https://${region}.console.aws.amazon.com/servicequotas/home/services/ec2/quotas`;
}

function getProviderCaseUrl(provider: string, caseId: string): string {
  if (provider === 'gcp') {
    return 'https://console.cloud.google.com/support/cases';
  }
  return `https://support.console.aws.amazon.com/support/home#/case/?displayId=${caseId}`;
}

function ExternalLinkIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={`inline-block w-3 h-3 ${className}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  );
}

function isStaleRequest(req: { status: string; requested_at: string | null }): boolean {
  return req.status === 'pending' && !!req.requested_at &&
    (Date.now() - new Date(req.requested_at).getTime()) > STALE_PENDING_MS;
}

interface QuotaRequest {
  id: number;
  provider: string;
  region: string;
  instance_family: string;
  current_limit: number;
  requested_limit: number;
  granted_limit: number | null;
  justification: string | null;
  provider_request_id: string | null;
  provider_case_id: string | null;
  status: string;
  status_detail: string | null;
  requested_at: string | null;
  requested_by: string | null;
  last_polled_at: string | null;
  resolved_at: string | null;
}

const STATUS_STYLES: Record<string, { bg: string; text: string; border: string; label: string }> = {
  submitted: { bg: 'bg-gray-800', text: 'text-gray-300', border: 'border-gray-600', label: 'Submitted' },
  pending: { bg: 'bg-yellow-900/30', text: 'text-yellow-300', border: 'border-yellow-700', label: 'Pending' },
  approved: { bg: 'bg-green-900/30', text: 'text-green-300', border: 'border-green-700', label: 'Approved' },
  denied: { bg: 'bg-red-900/30', text: 'text-red-300', border: 'border-red-700', label: 'Denied' },
  cancelled: { bg: 'bg-gray-800', text: 'text-gray-500', border: 'border-gray-700', label: 'Cancelled' },
  error: { bg: 'bg-red-900/20', text: 'text-red-400', border: 'border-red-800', label: 'Error' },
};

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export default function QuotaRequestHistory() {
  const [requests, setRequests] = useState<QuotaRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterProvider, setFilterProvider] = useState<string>('');

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(() => {
      fetchRequests();
    }, 60_000);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Re-fetch on filter change + 60s auto-poll with visibility pause
  useEffect(() => {
    fetchRequests();
    startPolling();

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchRequests();
        startPolling();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [filterStatus, filterProvider]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterProvider) params.set('provider', filterProvider);

      const response = await axios.get(
        `${getApiUrl()}/api/quotas/requests/history?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setRequests(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load request history');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="animate-spin h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-900/20 border border-red-800 rounded-lg text-red-400 text-sm">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center space-x-4">
        <select
          value={filterProvider}
          onChange={(e) => setFilterProvider(e.target.value)}
          className="bg-gray-800 text-white text-sm rounded-lg border border-gray-700 px-3 py-2 focus:border-blue-500 focus:outline-none"
        >
          <option value="">All Providers</option>
          <option value="aws">AWS</option>
          <option value="gcp">GCP</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-gray-800 text-white text-sm rounded-lg border border-gray-700 px-3 py-2 focus:border-blue-500 focus:outline-none"
        >
          <option value="">All Statuses</option>
          <option value="submitted">Submitted</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="denied">Denied</option>
          <option value="error">Error</option>
        </select>
        <button
          onClick={fetchRequests}
          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg transition-colors"
        >
          Refresh
        </button>
        <span className="text-gray-500 text-sm ml-auto">
          {requests.length} request{requests.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      {requests.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p className="text-lg mb-1">No quota increase requests</p>
          <p className="text-sm">Requests submitted via the quota dashboard will appear here</p>
        </div>
      ) : (
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-gray-400">
                <th className="text-left px-4 py-3 font-medium">Provider</th>
                <th className="text-left px-4 py-3 font-medium">Region</th>
                <th className="text-left px-4 py-3 font-medium">Family</th>
                <th className="text-left px-4 py-3 font-medium">Change</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Submitted</th>
                <th className="text-left px-4 py-3 font-medium">Resolved</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => {
                const style = STATUS_STYLES[req.status] || STATUS_STYLES.submitted;
                const isExpanded = expandedId === req.id;

                return (
                  <tr key={req.id} className="group">
                    <td colSpan={7} className="p-0">
                      {/* Main row */}
                      <div
                        className="grid grid-cols-7 px-4 py-3 border-b border-gray-800/50 hover:bg-gray-800/30 cursor-pointer transition-colors items-center"
                        onClick={() => toggleExpand(req.id)}
                      >
                        <div className="text-white font-medium uppercase">{req.provider}</div>
                        <div className="text-gray-300">{req.region}</div>
                        <div className="text-gray-300">
                          {(() => {
                            const group = getQuotaGroup(req.provider, req.instance_family);
                            if (group) {
                              return (
                                <span>
                                  <span className="font-medium">{group.label}</span>
                                  <span className="text-gray-500 text-xs ml-1.5">({req.instance_family})</span>
                                </span>
                              );
                            }
                            return <span className="uppercase">{req.instance_family}</span>;
                          })()}
                        </div>
                        <div className="text-gray-300">
                          {req.current_limit} → {req.granted_limit ?? req.requested_limit}
                        </div>
                        <div>
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${style.bg} ${style.text} ${style.border}`}>
                            {style.label}
                          </span>
                        </div>
                        <div className="text-gray-400 text-xs">{formatDate(req.requested_at)}</div>
                        <div className="text-gray-400 text-xs">{formatDate(req.resolved_at)}</div>
                      </div>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <div className="px-6 py-4 bg-gray-800/20 border-b border-gray-800 space-y-3">
                          {req.justification && (
                            <div>
                              <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Justification</p>
                              <p className="text-gray-300 text-sm">{req.justification}</p>
                            </div>
                          )}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {req.provider_request_id && (
                              <div>
                                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Provider Request ID</p>
                                <a
                                  href={getProviderRequestUrl(req.provider, req.region, req.provider_request_id)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-300 hover:text-blue-400 hover:underline text-xs font-mono break-all transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {req.provider_request_id} <ExternalLinkIcon className="ml-1 opacity-60" />
                                </a>
                              </div>
                            )}
                            {req.provider_case_id && (
                              <div>
                                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Case ID</p>
                                <a
                                  href={getProviderCaseUrl(req.provider, req.provider_case_id)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-300 hover:text-blue-400 hover:underline text-xs font-mono transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {req.provider_case_id} <ExternalLinkIcon className="ml-1 opacity-60" />
                                </a>
                              </div>
                            )}
                            {req.requested_by && (
                              <div>
                                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Requested By</p>
                                <p className="text-gray-300 text-sm">{req.requested_by}</p>
                              </div>
                            )}
                            {req.last_polled_at && (
                              <div>
                                <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Last Polled</p>
                                <p className="text-gray-300 text-xs">{formatDate(req.last_polled_at)}</p>
                              </div>
                            )}
                          </div>
                          {req.status_detail && (
                            <div>
                              <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Detail</p>
                              <p className="text-red-400 text-sm">{req.status_detail}</p>
                            </div>
                          )}
                          {isStaleRequest(req) && (
                            <a
                              href={req.provider_request_id
                                ? getProviderRequestUrl(req.provider, req.region, req.provider_request_id)
                                : req.provider === 'gcp'
                                  ? 'https://console.cloud.google.com/iam-admin/quotas'
                                  : `https://${req.region}.console.aws.amazon.com/servicequotas/home/services/ec2/quotas`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-yellow-400/80 hover:text-yellow-300 text-xs transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Check provider console for updates <ExternalLinkIcon />
                            </a>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
